import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Container } from '@mui/material';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/AuthService';
import { httpClient } from '@/utils/httpClient';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@equip/utils/logger';
import { useAuthStore, AuthorizationError } from '@/stores/useAuthStore';
import type { AuthResult } from '@/types/Auth';

const log = logger.child({ component: 'AuthCallback' });

// Session storage key used purely to signal "a token exchange is in progress"
// to useAuthStore.initializeAuth (so it keeps the loading state during a
// refresh instead of flashing the login UI). It is NOT the deduplication
// mechanism — see codeExchanges below.
const TOKEN_EXCHANGE_KEY = 'token_exchange_in_progress';

// In-memory single-flight for authorization-code exchange.
//
// The callback effect can run more than once for the same authorization code
// (React StrictMode double-mounts in dev, or a genuine component remount). An
// OAuth authorization code is single-use: sending it to Keycloak twice yields
// `invalid_code`, which surfaces to the user as "token exchange failed" — even
// when the FIRST exchange actually succeeded. (This also happens for users with
// no eQuip role: the first exchange succeeds but the role gate rejects it, so
// isAuthenticated never flips and the old retry loop eventually re-sent the
// consumed code.) Keying the in-flight — and settled — exchange promise by code
// guarantees the code is exchanged exactly once; every other caller awaits the
// same result. The settled promise is retained for the lifetime of the page so
// a late remount reuses the outcome instead of re-sending the consumed code.
const codeExchanges = new Map<string, Promise<AuthResult>>();

const exchangeCodeOnce = (code: string, state: string | null): Promise<AuthResult> => {
  const existing = codeExchanges.get(code);
  if (existing) return existing;
  const pending = authService.exchangeCodeForToken(code, state);
  codeExchanges.set(code, pending);
  return pending;
};

export const AuthCallbackPage: React.FC = () => {
  const { setTokens, isAuthenticated, clearAuth } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [initialIntendedRoute] = useState(() => {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem('intended_route');
  });

  useEffect(() => {
    let cancelled = false;
    const clearAuthFlowArtifacts = () => {
      sessionStorage.removeItem('auth_in_progress');
      sessionStorage.removeItem('silent_login');
      sessionStorage.removeItem('login_initiated');
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('pkce_verifier');
      sessionStorage.removeItem('auth_init_timestamp');
      sessionStorage.removeItem(TOKEN_EXCHANGE_KEY);
    };

    const handleCallback = async () => {
      // When loaded inside a hidden iframe (silent re-auth), relay the URL
      // parameters back to the parent window and do nothing else.  This keeps
      // the iframe inert — no sessionStorage writes, no navigation, no token
      // exchange — so an in-progress interactive login is never disturbed.
      let isIframe = false;
      try { isIframe = window !== window.top; } catch { isIframe = true; }
      if (isIframe) {
        const relayParams = new URLSearchParams(window.location.search);
        const relayCode = relayParams.get('code');
        const relayState = relayParams.get('state');
        const relayError = relayParams.get('error');
        if (relayCode && relayState) {
          window.parent.postMessage(
            { type: 'silent-auth-code', code: relayCode, state: relayState },
            window.location.origin,
          );
        } else {
          window.parent.postMessage(
            { type: 'silent-auth-error', error: relayError ?? (relayCode ? 'missing_state' : 'no_code') },
            window.location.origin,
          );
        }
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      const isSilentLoginAttempt = sessionStorage.getItem('silent_login') === 'true';

      // Signal to initializeAuth that an exchange is underway (keeps the
      // loading state during refresh). Deduplication is handled by the
      // in-memory single-flight (exchangeCodeOnce), not by this flag.
      sessionStorage.setItem(TOKEN_EXCHANGE_KEY, String(Date.now()));

      try {
        // Clear one-shot silent-login marker now that callback processing has started.
        sessionStorage.removeItem('silent_login');
        sessionStorage.removeItem('auth_in_progress');

        if (error) {
          // Silent login should fail quietly to avoid flashing auth error banners
          // during refresh when the app falls back to interactive sign-in.
          if (isSilentLoginAttempt) {
            log.debug('Silent login unavailable', { error, errorDescription });
            clearAuthFlowArtifacts();
            // Another callback may have already succeeded; do not clobber an authenticated session.
            if (useAuthStore.getState().isAuthenticated) return;
            localStorage.setItem('silent_sso_last_fail', Date.now().toString());
            clearAuth();
            if (cancelled) return;
            navigate('/login', { replace: true });
            return;
          }

          log.error('OAuth error', { error, errorDescription });
          clearAuthFlowArtifacts();
          clearAuth();
          if (cancelled) return;
          navigate('/login?error=' + encodeURIComponent(error), { replace: true });
          return;
        }

        if (!code) {
          if (isSilentLoginAttempt) {
            log.debug('Silent login callback missing authorization code');
            clearAuthFlowArtifacts();
            // Another callback may have already succeeded; do not clobber an authenticated session.
            if (useAuthStore.getState().isAuthenticated) return;
            localStorage.setItem('silent_sso_last_fail', Date.now().toString());
            clearAuth();
            if (cancelled) return;
            navigate('/login', { replace: true });
            return;
          }
          log.error('No authorization code received');
          clearAuthFlowArtifacts();
          clearAuth();
          if (cancelled) return;
          navigate('/login?error=no_code', { replace: true });
          return;
        }

        // Exchange code for tokens. Single-flight: if this effect runs twice
        // for the same code (StrictMode / remount), both awaits share one
        // exchange so the single-use code is never re-sent to Keycloak.
        const tokens = await exchangeCodeOnce(code, state);

        // Only the live (non-cancelled) effect run should apply tokens and
        // navigate. In StrictMode the first run is cancelled during cleanup;
        // bailing here prevents a duplicate setTokens/provisioning/navigation.
        if (cancelled) return;

        await setTokens(tokens);

        // Ensure the user is provisioned in the DB. Fire-and-forget — do not
        // block navigation on this; a failure here is non-fatal.
        httpClient.post('/api/v1/users/me', {}).catch((err: unknown) => {
          log.warn('User provisioning request failed', { error: String(err) });
        });

        // Clear any lingering auth session artifacts
        sessionStorage.removeItem('used_codes');

        log.info('Authentication successful');

        // Invalidate and refetch protected queries after login
        queryClient.invalidateQueries();
        if (initialIntendedRoute) {
          sessionStorage.removeItem('intended_route');
          if (cancelled) return;
          navigate(initialIntendedRoute, { replace: true });
        } else {
          if (cancelled) return;
          navigate('/', { replace: true });
        }
      } catch (error) {
        if (useAuthStore.getState().isAuthenticated) {
          // Another callback handler may have already completed authentication.
          // Ignore late failures to avoid flashing transient login errors.
          log.debug('Ignoring callback error after authentication already succeeded', {
            error: String(error),
          });
          clearAuthFlowArtifacts();
          if (cancelled) return;
          navigate('/', { replace: true });
          return;
        }

        if (error instanceof AuthorizationError) {
          // Valid token, but the identity has no eQuip access — deny sign-in
          // and explain why rather than silently bouncing to the login form.
          log.warn('Login denied: identity has no eQuip application role');
          clearAuthFlowArtifacts();
          clearAuth();
          if (cancelled) return;
          navigate('/login?error=access_denied', { replace: true });
          return;
        }

        if (isSilentLoginAttempt) {
          log.debug('Silent login token exchange failed', { error: String(error) });
          clearAuthFlowArtifacts();
          localStorage.setItem('silent_sso_last_fail', Date.now().toString());
          clearAuth();
          if (cancelled) return;
          navigate('/login', { replace: true });
          return;
        }

        log.error('Token exchange failed', { error: String(error) });
        clearAuthFlowArtifacts();
        clearAuth();
        if (cancelled) return;
        navigate('/login?error=token_exchange_failed', { replace: true });
      } finally {
        // Clear the in-progress signal. Deduplication is handled by the
        // in-memory single-flight, so no delayed cleanup is required.
        sessionStorage.removeItem(TOKEN_EXCHANGE_KEY);
      }
    };

    handleCallback();
    return () => {
      cancelled = true;
    };
  }, [clearAuth, initialIntendedRoute, navigate, queryClient, setTokens]);

  // Redirect if already authenticated (tokens were set successfully)
  if (isAuthenticated && !initialIntendedRoute) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h6" gutterBottom>
          Completing sign in...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we verify your credentials
        </Typography>
      </Box>
    </Container>
  );
};
